import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { STATUS_STYLE, STATUS_LABEL, inr } from "@/lib/branch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShareLinks } from "@/components/ShareLinks";
import { BranchTree } from "@/components/NetworkTree";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");

export default function BranchView() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/branches/${id}`).then(({ data }) => setB(data)).catch((e) => setErr(e?.response?.data?.detail || "Branch not found")); }, [id]);

  if (err) return <div className="p-10 text-center text-red-600" data-testid="branch-view-error">{err}</div>;
  if (!b) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" /></div>;
  const tab = params.get("tab") || "overview";

  return (
    <div className="space-y-6" data-testid="branch-view-page">
      <Link to="/branches" className="inline-flex items-center gap-1 text-sm text-[#B91C1C]" data-testid="branch-view-back"><ArrowLeft className="w-4 h-4" /> My Branches</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-bold text-[#7F1D1D]">{b.name}</h1>
        <span className={`text-xs px-2 py-1 rounded-lg border ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span>
      </div>
      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList className="flex-wrap h-auto">
          {["overview", "network", "orders", "earnings"].map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`branch-tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><Overview b={b} /></TabsContent>
        <TabsContent value="network">
          <div className="bg-white rounded-2xl border border-[#F1F5F9] p-5">
            {b.status === "active" || b.direct_referrals ? <BranchTree code={b.referral_code} /> : <p className="text-sm text-[#94A3B8]">Network opens once this branch is active.</p>}
          </div>
        </TabsContent>
        <TabsContent value="orders"><BranchOrders id={id} /></TabsContent>
        <TabsContent value="earnings"><BranchEarnings id={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-2 border-b border-[#F1F5F9] text-sm"><span className="text-[#64748B]">{k}</span><span className="font-medium text-[#7F1D1D] text-right">{v}</span></div>;

const Overview = ({ b }) => (
  <div className="grid lg:grid-cols-2 gap-4">
    <div className="bg-white rounded-2xl border border-[#F1F5F9] p-5" data-testid="branch-overview-details">
      <Row k="Branch ID" v={b.branch_code} /><Row k="Referral code" v={b.referral_code} />
      <Row k="Created" v={fmt(b.created_at)} /><Row k="Activated" v={fmt(b.activated_at)} />
      <Row k="Activation order" v={b.activation_order ? `${b.activation_order.order_code} · ${b.activation_order.payment_status}` : "—"} />
      <Row k="Original sponsor" v={b.original_sponsor ? `${b.original_sponsor.name} (${b.original_sponsor.user_code})` : "—"} />
      <Row k="Sponsor branch" v={b.original_sponsor_branch_code || "—"} />
    </div>
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {[["Direct referrals", b.direct_referrals], ["Indirect referrals", b.indirect_referrals], ["Downline members", b.downline_members],
          ["Verified orders", b.verified_orders], ["Eligible sales", inr(b.eligible_sales)], ["Earned commission", inr(b.earned_commission)]].map(([k, v]) => (
          <div key={k} className="bg-white rounded-2xl border border-[#F1F5F9] p-4"><div className="text-[11px] uppercase text-[#94A3B8]">{k}</div><div className="font-numbers text-lg font-bold text-[#7F1D1D]">{v}</div></div>
        ))}
      </div>
      {b.status === "active" && <div className="bg-white rounded-2xl border border-[#F1F5F9] p-5"><h3 className="font-semibold text-[#7F1D1D] mb-2">Referral link</h3><ShareLinks code={b.referral_code} /></div>}
    </div>
  </div>
);

const Pager = ({ page, total, size = 20, onPage }) => total > size ? (
  <div className="flex gap-2 items-center text-xs p-3">
    <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
    <span>Page {page} / {Math.ceil(total / size)}</span>
    <button disabled={page * size >= total} onClick={() => onPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
  </div>
) : null;

const BranchOrders = ({ id }) => {
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/branches/${id}/orders`, { params: { page } }).then(({ data }) => setD(data)); }, [id, page]);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-[#DC2626] m-6" />;
  return (
    <div className="bg-white rounded-2xl border border-[#F1F5F9] overflow-x-auto" data-testid="branch-orders-table">
      <table className="w-full text-sm">
        <thead className="bg-[#FEF2F2] text-xs uppercase text-[#7F1D1D]"><tr>{["Order", "Buyer", "Type", "Amount", "Paid on"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {d.orders.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#94A3B8]">No verified orders from this branch's network yet.</td></tr>}
          {d.orders.map((o) => <tr key={o.id} className="border-t border-[#F1F5F9]"><td className="px-3 py-2 font-mono text-xs">{o.order_code}</td><td className="px-3 py-2">{o.buyer?.name} <span className="text-xs text-[#94A3B8]">{o.buyer?.user_code}</span></td><td className="px-3 py-2 text-xs">{o.is_joining_order ? "Hamper" : "Product"}</td><td className="px-3 py-2 font-numbers">{inr(o.total)}</td><td className="px-3 py-2 text-xs">{fmt(o.paid_at)}</td></tr>)}
        </tbody>
      </table>
      <Pager page={page} total={d.total} onPage={setPage} />
    </div>
  );
};

const BranchEarnings = ({ id }) => {
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/branches/${id}/earnings`, { params: { page } }).then(({ data }) => setD(data)); }, [id, page]);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-[#DC2626] m-6" />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="branch-earnings-totals">
        {[["Earned", inr(d.totals.earned)], ["Reversed", inr(d.totals.reversed)], ["Net approved", inr(d.totals.approved)], ["Held events", d.totals.held_events]].map(([k, v]) => (
          <div key={k} className="bg-white rounded-2xl border border-[#F1F5F9] p-4"><div className="text-[11px] uppercase text-[#94A3B8]">{k}</div><div className="font-numbers text-lg font-bold text-[#7F1D1D]">{v}</div></div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-[#F1F5F9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FEF2F2] text-xs uppercase text-[#7F1D1D]"><tr>{["Date", "Type", "Level", "Description", "Amount"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {d.items.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#94A3B8]">No commission entries for this branch yet.</td></tr>}
            {d.items.map((t) => <tr key={t.id} className="border-t border-[#F1F5F9]"><td className="px-3 py-2 text-xs">{fmt(t.created_at)}</td><td className="px-3 py-2 text-xs">{t.category}</td><td className="px-3 py-2">{t.level ? `L${t.level}` : "—"}</td><td className="px-3 py-2 text-xs">{t.description}</td><td className={`px-3 py-2 font-numbers ${t.type === "debit" ? "text-red-600" : ""}`}>{t.type === "debit" ? "-" : ""}{inr(t.amount)}</td></tr>)}
          </tbody>
        </table>
        <Pager page={page} total={d.total} onPage={setPage} />
      </div>
    </div>
  );
};
